import { Moon, Sun, Palette } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';

type ThemeMode = 'green-light' | 'green-dark' | 'general-light' | 'general-dark';

const CYCLE: ThemeMode[] = ['green-light', 'green-dark', 'general-light', 'general-dark'];

const LABELS: Record<ThemeMode, string> = {
  'green-light': 'Green Light',
  'green-dark': 'Green Dark',
  'general-light': 'General Light',
  'general-dark': 'General Dark',
};

function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;
  root.classList.toggle('dark', mode === 'green-dark' || mode === 'general-dark');
  root.classList.toggle('theme-general', mode === 'general-light' || mode === 'general-dark');
  localStorage.setItem('theme-mode', mode);
  // Legacy key kept in sync for anything still reading `theme`
  localStorage.setItem('theme', mode.endsWith('dark') ? 'dark' : 'light');
}

function readInitial(): ThemeMode {
  if (typeof window === 'undefined') return 'green-light';
  const saved = localStorage.getItem('theme-mode') as ThemeMode | null;
  if (saved && CYCLE.includes(saved)) return saved;
  const legacy = localStorage.getItem('theme');
  if (legacy === 'dark') return 'green-dark';
  return 'green-light';
}

export function DarkModeToggle() {
  const [mode, setMode] = useState<ThemeMode>(readInitial);

  useEffect(() => {
    applyTheme(mode);
  }, [mode]);

  const next = () => {
    const idx = CYCLE.indexOf(mode);
    setMode(CYCLE[(idx + 1) % CYCLE.length]);
  };

  const isDark = mode === 'green-dark' || mode === 'general-dark';
  const isGeneral = mode === 'general-light' || mode === 'general-dark';

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={next}
      className="rounded-full"
      aria-label={`Theme: ${LABELS[mode]}. Click to switch.`}
      title={LABELS[mode]}
    >
      {isGeneral ? (
        <Palette className="w-5 h-5" />
      ) : isDark ? (
        <Sun className="w-5 h-5" />
      ) : (
        <Moon className="w-5 h-5" />
      )}
    </Button>
  );
}
